// The HTTP contract with AssistantController (EZatWork.Models/DTOs/AssistantDtos.cs).
// JSON is camelCase.

export interface AssistantConfig {
  enabled: boolean;
  name: string;
  maxMessagesPerConversation: number;
  /** A receipt can be attached in the chat. */
  receipts?: boolean;
  /** Messages left this month on the Free plan; null/absent where there is no monthly limit. */
  monthlyMessagesLeft?: number | null;
}

export interface AssistantMessageRequest {
  /** Null/absent starts a new conversation. */
  conversationId?: string | null;
  /** Optional when an attachment is sent. */
  message: string;
  uiLanguage?: string;
  /** A receipt uploaded and read through POST /api/receipts/scan. */
  attachmentId?: string;
}

export interface AssistantField {
  /** Labelled from i18n key assistant.fields.{key}. */
  key: string;
  value: string;
}

export interface AssistantPendingAction {
  id: string;
  /** Titled from i18n key assistant.actions.{tool}. */
  tool: string;
  fields: AssistantField[];
}

export interface AssistantReply {
  conversationId: string;
  reply: string;
  pendingAction?: AssistantPendingAction | null;
  messagesLeft: number;
  /** As in AssistantConfig, after this message. */
  monthlyMessagesLeft?: number | null;
}

/** POST /api/assistant/feedback: asked for when the user leaves a conversation. */
export interface AssistantFeedbackRequest {
  conversationId?: string | null;
  /** 1 to 5. */
  rating: number;
  comment?: string;
  uiLanguage?: string;
}

export type AssistantResultCode = 'done' | 'cancelled' | 'failed' | 'not_pending';

export interface AssistantActionResult {
  ok: boolean;
  code: AssistantResultCode | string;
  /** An app route to what was created, e.g. /invoices/123. */
  link?: string | null;
}

export type AssistantErrorCode =
  | 'assistant_disabled'
  | 'limit_conversation'
  | 'limit_monthly'
  | 'limit_daily'
  | 'rate_limited'
  | 'model_unavailable'
  | 'action_not_pending'
  | 'invalid_message'
  | 'not_found'
  | 'invalid_attachment'
  | 'generic';

export const ASSISTANT_ERROR_CODES: readonly AssistantErrorCode[] = [
  'assistant_disabled',
  'limit_conversation',
  'limit_monthly',
  'limit_daily',
  'rate_limited',
  'model_unavailable',
  'action_not_pending',
  'invalid_message',
  'not_found',
  'invalid_attachment',
  'generic',
];

export const ASSISTANT_MAX_MESSAGE_LENGTH = 2000;

/** The state of a confirmation card in the transcript. */
export type ActionCardState = 'pending' | 'busy' | 'resolved';

export interface ChatActionCard {
  action: AssistantPendingAction;
  state: ActionCardState;
  /** assistant.results.{code} once resolved. */
  resultCode?: string;
  link?: string | null;
  /** A request error while confirming/cancelling; the buttons stay usable. */
  errorCode?: AssistantErrorCode;
}

/**
 * A file on a user message: its name and kind only. The picture itself stays
 * in memory (AssistantChatStore.thumbnails) and is never written to storage.
 */
export interface ChatAttachment {
  name: string;
  kind: 'image' | 'pdf';
}

export type ChatEntry =
  | { kind: 'user'; id: number; text: string; attachment?: ChatAttachment }
  | { kind: 'assistant'; id: number; text: string; card?: ChatActionCard }
  | { kind: 'error'; id: number; code: AssistantErrorCode }
  /** A receipt that could not be read; worded from assistant.receipt.errors.{code}. */
  | { kind: 'receipt_error'; id: number; code: ReceiptErrorCode };

// ---------- receipts (ReceiptsController, EZatWork.Models/DTOs/ReceiptDtos.cs) ----------

export interface ReceiptExtraction {
  isReceipt: boolean;
  supplier?: string | null;
  supplierTaxId?: string | null;
  documentType?: string | null;
  documentNumber?: string | null;
  date?: string | null;
  currency?: string | null;
  total?: number | null;
  vatAmount?: number | null;
  netAmount?: number | null;
  itemsSummary?: string | null;
  suggestedCategory?: string | null;
  confidence?: number | null;
}

/** POST /api/receipts/scan. */
export interface ReceiptScanResult {
  attachmentId: string;
  fileName?: string;
  contentType?: string;
  /** False in a demo: the file was read and not kept. */
  stored: boolean;
  extraction?: ReceiptExtraction | null;
  matchedCategoryId?: number | null;
  matchedCategoryName?: string | null;
  scansLeft?: number | null;
}

export type ReceiptErrorCode =
  | 'invalid_file'
  | 'file_too_large'
  | 'plan_limit'
  | 'limit_daily'
  | 'scan_unavailable'
  | 'receipts_disabled'
  | 'generic';

export const RECEIPT_ERROR_CODES: readonly ReceiptErrorCode[] = [
  'invalid_file', 'file_too_large', 'plan_limit', 'limit_daily', 'scan_unavailable', 'receipts_disabled', 'generic',
];

/** Images (the browser's own and HEIC from phones) and PDF, as the server accepts. */
export const RECEIPT_ACCEPT = 'image/*,application/pdf';
